/*
  lab-collection.js
  The collection filter, two modes off one markup:

  - Preview (window.LAB_COLLECTION_PREVIEW, injected by the static
    renderer): filters and sorts the already-rendered cards in the
    browser, so the demo feels live with no network.
  - Live Shopify (no flag): the filter drawer and sort build a
    /collections/<handle>?filter... URL and navigate, so Shopify filters
    server-side. Progressive: the form and chip links work without JS.

  Zara-clean: options never show counts.
*/
(function () {
  'use strict';

  var PREVIEW = typeof window.LAB_COLLECTION_PREVIEW !== 'undefined';

  function q(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  var root, drawer, scrim, form, grid, countEl, activeEl, noResults, sortSelect, filterDot;
  var cells = [];
  var lastFocused = null;
  var isOpen = false;

  /* ---------------------------------------------------------------- */
  /* Drawer open / close / focus trap                                  */
  /* ---------------------------------------------------------------- */

  function openDrawer() {
    if (!drawer || isOpen) return;
    isOpen = true;
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.classList.add('is-open');
    document.documentElement.classList.add('lab-filter-lock');
    var close = q('[data-lab-filter-close]', drawer);
    if (close) close.focus();
  }

  function closeDrawer() {
    if (!drawer || !isOpen) return;
    isOpen = false;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    if (scrim) scrim.classList.remove('is-open');
    document.documentElement.classList.remove('lab-filter-lock');
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function focusables() {
    return qa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', drawer)
      .filter(function (n) { return n.offsetParent !== null; });
  }

  function onKeydown(event) {
    if (!isOpen) return;
    if (event.key === 'Escape') { closeDrawer(); return; }
    if (event.key !== 'Tab') return;
    var items = focusables();
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1], active = document.activeElement;
    if (event.shiftKey && (active === first || !drawer.contains(active))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
  }

  /* ---------------------------------------------------------------- */
  /* Shared: read the checked filters as { param: [values] }           */
  /* ---------------------------------------------------------------- */

  function checkedGroups() {
    var groups = {};
    qa('[data-lab-filter-value]:checked', form).forEach(function (cb) {
      var p = cb.getAttribute('data-filter-param');
      (groups[p] = groups[p] || []).push(cb.value);
    });
    return groups;
  }

  function anyChecked() {
    return !!q('[data-lab-filter-value]:checked', form);
  }

  /* ---------------------------------------------------------------- */
  /* Preview mode: client-side filter + sort                           */
  /* ---------------------------------------------------------------- */

  function priceInBucket(price, bucket) {
    var parts = bucket.split('-');
    var min = parseInt(parts[0], 10) || 0;
    var max = parts[1] === '' || parts[1] === undefined ? Infinity : parseInt(parts[1], 10);
    return price >= min && price < max;
  }

  function key(param) {
    // 'filter.color' -> 'color'
    var dot = param.lastIndexOf('.');
    return dot === -1 ? param : param.slice(dot + 1);
  }

  function cellMatches(cell, groups) {
    for (var param in groups) {
      if (!groups.hasOwnProperty(param)) continue;
      var vals = groups[param];
      var k = key(param);
      var ok = false;
      if (k === 'availability') {
        ok = cell.getAttribute('data-available') === 'true';
      } else if (k === 'type') {
        ok = vals.indexOf(cell.getAttribute('data-type') || '') !== -1;
      } else if (k === 'color' || k === 'size') {
        var tokens = (cell.getAttribute('data-' + k) || '').split(' ');
        ok = vals.some(function (v) { return tokens.indexOf(v) !== -1; });
      } else if (k === 'price') {
        var price = parseInt(cell.getAttribute('data-price'), 10) || 0;
        ok = vals.some(function (v) { return priceInBucket(price, v); });
      } else {
        ok = true;
      }
      if (!ok) return false; // AND across groups
    }
    return true;
  }

  function updateCount(n) {
    if (countEl) countEl.textContent = n === 1 ? '1 piece' : n + ' pieces';
  }

  function buildChips(groups) {
    if (!activeEl) return;
    activeEl.innerHTML = '';
    qa('[data-lab-filter-value]:checked', form).forEach(function (cb) {
      var labelEl = cb.parentNode.querySelector('.lab-filter__option-label');
      var label = labelEl ? labelEl.textContent : cb.value;
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'lab-main-collection__chip tracked-caps';
      chip.setAttribute('data-lab-chip', '');
      chip.innerHTML = label + ' <span class="lab-main-collection__chip-x" aria-hidden="true">×</span>';
      chip.addEventListener('click', function () { cb.checked = false; applyPreview(); });
      activeEl.appendChild(chip);
    });
  }

  function applyPreview() {
    var groups = checkedGroups();
    var visible = 0;
    cells.forEach(function (cell) {
      var show = cellMatches(cell, groups);
      cell.hidden = !show;
      if (show) visible += 1;
    });
    updateCount(visible);
    if (noResults) noResults.hidden = visible !== 0;
    if (filterDot) filterDot.hidden = !anyChecked();
    buildChips(groups);
  }

  function sortPreview(value) {
    var arr = cells.slice();
    if (value === 'price-ascending') {
      arr.sort(function (a, b) { return num(a, 'data-price') - num(b, 'data-price'); });
    } else if (value === 'price-descending') {
      arr.sort(function (a, b) { return num(b, 'data-price') - num(a, 'data-price'); });
    } else if (value === 'title-ascending') {
      arr.sort(function (a, b) { return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || ''); });
    } else {
      arr.sort(function (a, b) { return num(a, 'data-index') - num(b, 'data-index'); });
    }
    arr.forEach(function (cell) { grid.appendChild(cell); });
    demoteSoldOut();
  }

  function num(el, attr) { return parseInt(el.getAttribute(attr), 10) || 0; }

  /* Sold-out cells sink to the end of the grid in their original
     relative order, whatever the sort. Runs on load in both modes and
     again after every preview sort. */
  function demoteSoldOut() {
    if (!grid) return;
    qa('[data-lab-cell][data-available="false"]', grid).forEach(function (cell) {
      grid.appendChild(cell);
    });
  }

  /* ---------------------------------------------------------------- */
  /* Live Shopify mode: build a filter URL and navigate                */
  /* ---------------------------------------------------------------- */

  function liveNavigate() {
    var params = new URLSearchParams();
    qa('[data-lab-filter-value]:checked', form).forEach(function (cb) { params.append(cb.name, cb.value); });
    qa('[data-lab-price-min], [data-lab-price-max]', form).forEach(function (inp) {
      if (inp.value) params.append(inp.name, inp.value);
    });
    if (sortSelect && sortSelect.value) params.set('sort_by', sortSelect.value);
    var base = (form && form.getAttribute('action')) || window.location.pathname;
    var qs = params.toString();
    window.location.assign(qs ? base + '?' + qs : base);
  }

  /* ---------------------------------------------------------------- */
  /* Wiring                                                            */
  /* ---------------------------------------------------------------- */

  function onDocumentClick(event) {
    if (event.target.closest('[data-lab-filter-open]')) { openDrawer(); return; }
    if (event.target.closest('[data-lab-filter-close]')) { closeDrawer(); return; }
    if (event.target.closest('[data-lab-filter-scrim]')) { closeDrawer(); return; }
  }

  function init() {
    root = q('[data-lab-collection]');
    if (!root) return;
    drawer = q('[data-lab-filter-drawer]');
    scrim = q('[data-lab-filter-scrim]');
    form = q('[data-lab-filter-form]');
    grid = q('[data-lab-collection-grid]');
    countEl = q('[data-lab-collection-count]');
    activeEl = q('[data-lab-active-filters]');
    noResults = q('[data-lab-no-results]');
    sortSelect = q('[data-lab-sort]');
    filterDot = q('[data-lab-filter-dot]');
    cells = grid ? qa('[data-lab-cell]', grid) : [];
    demoteSoldOut();

    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onKeydown);

    if (PREVIEW) {
      if (form) {
        form.addEventListener('change', applyPreview);
        form.addEventListener('submit', function (e) { e.preventDefault(); closeDrawer(); });
        var clear = q('[data-lab-filter-clear]', form);
        if (clear) clear.addEventListener('click', function (e) {
          e.preventDefault();
          qa('[data-lab-filter-value]', form).forEach(function (cb) { cb.checked = false; });
          applyPreview();
        });
      }
      if (sortSelect) sortSelect.addEventListener('change', function () { sortPreview(sortSelect.value); });
      var sortForm = q('[data-lab-sort-form]');
      if (sortForm) sortForm.addEventListener('submit', function (e) { e.preventDefault(); });
    } else {
      if (form) {
        form.addEventListener('submit', function (e) { e.preventDefault(); liveNavigate(); });
      }
      if (sortSelect) sortSelect.addEventListener('change', liveNavigate);
      var sortFormLive = q('[data-lab-sort-form]');
      if (sortFormLive) sortFormLive.addEventListener('submit', function (e) { e.preventDefault(); liveNavigate(); });
      // Clear all + chip removes keep their native hrefs (full reload).
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
